import React, { useEffect, useMemo, useState } from "react";
import { Box, Flex, Text, Alert, AlertDescription, IconButton, Tooltip, Spinner, ModalBody } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { User, getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import { envConfig } from "@/config";
import { colours } from "@/utils/themeUtils";
import { StudentSearchSelector } from "./StudentSearchSelector";
import { useToast } from "@/components/ui/Toast";
import { userDisplayText } from "@/js/utils/userUtils";
import { UserInfo } from "@/js/api/authApi";
import { getUserDetail } from "@/js/api/usersApi";
import { getClassroomDisplayName, isClassroomSynced } from "@/utils/classroomUtils";
import { addStudentToClassroom, removeStudentFromClassroom } from "@/api/modifyStudents";
import { useQueryClient } from "@tanstack/react-query";
import { makeClassroomDetailsInvalidationKey } from "@/query/classroomDetailsQuery";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { getClassroomDetails } from "@/js/api/classApi";
import { ClasswizeModal } from "./ui/Modal";
import { ChakraFAIcon } from "./ui/Icon";

interface Props {
	isOpen: boolean;
	onClose: () => void;

	isMonitoringClass: boolean;
	students?: User[];
	classroom: ClassroomOverviewDTO;

	/**
	 * Optional callback - Allows the parent component to perform an action when the students are modified (such as fetching fresh data).
	 */
	studentsModifiedCallback?: (addedStudents: User[], removedStudents: User[]) => void;

	/**
	 * Optional callback - Allows the parent component to refresh the student claims when the students are modified.
	 */
	handleRefreshStudentClaims?: () => void;
}

export function ModifyStudentsModal(props: Props): JSX.Element {
	const { t } = useTranslation();
	const toast = useToast();

	const [selectedStudents, setSelectedStudents] = useState<User[]>(props.students ?? []);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const [isLoadingStudents, setIsLoadingStudents] = useState<boolean>(false);

	const userSessionStore = useUserSessionStore();
	const teacherId = userSessionStore.getTeacherId() || "";

	const queryClient = useQueryClient();

	useEffect(() => {
		// We don't have the students to pass as a prop from on the overview page.
		// We also don't want to fetch all the students for all the classes when not needed.
		// So this will fetch the students for the class if they're not already provided and only when the modal is open.
		if (!props.isOpen) return;

		// Fetch students if they're not provided
		if (!props.students) {
			setIsLoadingStudents(true);
			getClassroomDetails(
				props.classroom.id || (props.classroom.name as string),
				true,
				false,
				false,
				props.classroom.type,
				props.classroom.sourceId,
				useUserSessionStore.getState().getTeacherId() as string
			)
				.then((c) => {
					setSelectedStudents(c.classroom.students ?? []);
				})
				.finally(() => {
					setIsLoadingStudents(false);
				});
		}
	}, [props.isOpen, props.classroom.id, props.classroom.name, props.classroom.sourceId, props.classroom.type, props.students]);

	// Update selected students when props.students changes
	useEffect(() => {
		setSelectedStudents(props.students ?? []);
	}, [props.students]);

	function handleStudentsModified(addedStudents: UserInfo[], removedStudents: UserInfo[]) {
		if (props.handleRefreshStudentClaims) {
			props.handleRefreshStudentClaims();
		}

		if (props.studentsModifiedCallback) {
			props.studentsModifiedCallback(addedStudents, removedStudents);
		}

		// Update the list of classroom users for the active classroom.
		queryClient.invalidateQueries(makeClassroomDetailsInvalidationKey(props.classroom.id));
	}

	function handleAddStudent(student: UserInfo) {
		if (props.isMonitoringClass && selectedStudents.length >= envConfig.MONITOR_MODE_MAX_STUDENTS) {
			toast.errorToast({
				title: t("modifyStudents.toastMonitorMax", { max: envConfig.MONITOR_MODE_MAX_STUDENTS }),
			});
			return;
		}

		if (selectedStudents.some((s) => s.stableIdV1 === student.stableIdV1)) {
			toast.errorToast({
				title: t("modifyStudents.toastAlreadyInClass", {
					user: userDisplayText(student),
					classroom: getClassroomDisplayName(props.classroom),
				}),
			});
			return;
		}

		setIsSaving(true);

		getUserDetail(student.username, false, false, true)
			.then((user) => {
				if (!user) {
					toast.errorToast({
						title: t("modifyStudents.toastFailedAddTitle", {
							user: userDisplayText(student),
							classroom: getClassroomDisplayName(props.classroom),
						}),
						description: t("errors.unexpectedError"),
					});
					setIsSaving(false);
					return;
				}

				if (!user.isStudent) {
					// The `isStudent` property will be false if the user is a teacher, or if they are added to the list of "restricted users" in School Manager.
					toast.errorToast({
						title: t("modifyStudents.toastCouldNotAddTitle", {
							user: userDisplayText(student),
							classroom: getClassroomDisplayName(props.classroom),
						}),
						description: t("modifyStudents.toastCouldNotAddDescription", { user: userDisplayText(student) }),
					});
					setIsSaving(false);
					return;
				}

				addStudentToClassroom(
					teacherId,
					user.qoriaUserId,
					props.classroom.stableIdV1,
					props.classroom.type.toLowerCase() as "classroom" | "group"
				)
					.then(() => {
						setSelectedStudents((prev) => [...prev, student]);
						handleStudentsModified([student], []);
						toast.successToast({
							isClosable: true,
							title: t("modifyStudents.toastSuccessAdded", {
								user: userDisplayText(student),
								classroom: getClassroomDisplayName(props.classroom),
							}),
						});
					})
					.catch((error) => {
						console.error("Failed to add student to class:", error);
						toast.errorToast({
							title: t("modifyStudents.toastFailedAddTitle", {
								user: userDisplayText(student),
								classroom: getClassroomDisplayName(props.classroom),
							}),
							description: t("errors.unexpectedError"),
						});
					});
			})
			.catch((error) => {
				console.error("Failed to add student to class:", error);
				toast.errorToast({
					title: t("modifyStudents.toastFailedAddTitle", {
						user: userDisplayText(student),
						classroom: getClassroomDisplayName(props.classroom),
					}),
					description: t("errors.unexpectedError"),
				});
				return;
			})
			.finally(() => {
				setIsSaving(false);
			});
	}

	function handleRemoveStudent(student: User) {
		setIsSaving(true);

		getUserDetail(student.username, false, false, true)
			.then((user) => {
				if (!user) {
					toast.errorToast({
						title: t("modifyStudents.toastFailedAddTitle", {
							user: userDisplayText(student),
							classroom: getClassroomDisplayName(props.classroom),
						}),
						description: t("errors.unexpectedError"),
					});
					setIsSaving(false);
					return;
				}

				removeStudentFromClassroom(
					teacherId,
					user.qoriaUserId,
					props.classroom.type.toLowerCase() as "classroom" | "group",
					props.classroom.stableIdV1
				)
					.then(() => {
						setSelectedStudents((prev) => prev.filter((s) => s.stableIdV1 !== user.stableIdV1));
						handleStudentsModified([], [user]);
						toast.successToast({
							isClosable: true,
							title: t("modifyStudents.toastSuccessRemoved", {
								user: userDisplayText(user),
								classroom: getClassroomDisplayName(props.classroom),
							}),
						});
					})
					.catch((error) => {
						console.error("Failed to remove student from class:", error);
						toast.errorToast({
							title: t("modifyStudents.toastFailedRemoveTitle", {
								user: userDisplayText(user),
								classroom: getClassroomDisplayName(props.classroom),
							}),
							description: t("errors.unexpectedError"),
						});
					})
					.finally(() => {
						setIsSaving(false);
					});
			})
			.catch((error) => {
				console.error("Failed to add student to class:", error);
				toast.errorToast({
					title: t("modifyStudents.toastFailedAddTitle", {
						user: userDisplayText(student),
						classroom: getClassroomDisplayName(props.classroom),
					}),
					description: t("errors.unexpectedError"),
				});
				return;
			});
	}

	const sortedStudents = useMemo(() => {
		return [...selectedStudents].sort((a, b) => a.username.localeCompare(b.username));
	}, [selectedStudents]);

	return (
		<ClasswizeModal
			isOpen={props.isOpen}
			onClose={props.onClose}
			size={"2xl"}
			title={t("modifyStudents.title")}
			dataTestId={"modify-students-modal"}
		>
			<ModalBody>
				<Flex direction={"column"} gap={6}>
					<Text color={colours.navy[800]} lineHeight={"20px"}>
						{t("modifyStudents.intro")}
					</Text>

					{isClassroomSynced(props.classroom) && (
						<Text fontSize={"14px"} color={colours.danger.icon} lineHeight={"20px"} fontWeight={500}>
							{t("modifyStudents.syncOverrideWarning")}
						</Text>
					)}

					{selectedStudents.length > envConfig.MAX_STUDENTS && (
						<Flex>
							<Alert status="warning" bgColor="#FFE8EA" mb="16px" pt="12px" width="unset">
								<Box mx="8px" pt="-4px">
									<ChakraFAIcon icon="fa-triangle-exclamation" variant="solid" color="#FF0000" />
								</Box>
								<AlertDescription fontSize="14px" fontWeight="500" pt="2px">
									{t("modifyStudents.maxStudentsAlert", { count: selectedStudents.length, max: envConfig.MAX_STUDENTS })}
								</AlertDescription>
							</Alert>
						</Flex>
					)}

					{isLoadingStudents ? (
						<Flex alignItems="center" justifyContent="center" gap={2.5} my={2.5}>
							<Spinner />
							<Text color={colours.text.body} fontSize={"14px"} lineHeight={"20px"}>
								{t("modifyStudents.loadingStudents")}
							</Text>
						</Flex>
					) : (
						<>
							<Flex alignItems={"center"} justifyContent={"space-between"} mb={-5} px={1}>
								<Text fontWeight="medium" color={"neutrals.800"}>
									{t("modifyStudents.studentsHeading")}
								</Text>
								{props.isMonitoringClass && (
									<Text color={"neutrals.300"}>
										<Text
											as="span"
											fontWeight={selectedStudents.length > envConfig.MONITOR_MODE_MAX_STUDENTS ? 500 : 400}
											color={selectedStudents.length > envConfig.MONITOR_MODE_MAX_STUDENTS ? colours.danger.icon : "inherit"}
										>
											{selectedStudents.length}
										</Text>
										{t("modifyStudents.monitorSelectedSuffix", { max: envConfig.MONITOR_MODE_MAX_STUDENTS })}
									</Text>
								)}
							</Flex>
							<StudentSearchSelector selectStudent={handleAddStudent} />
							<Box maxHeight={"300px"} overflowY={"auto"} mt={-4}>
								{!selectedStudents || selectedStudents.length === 0 ? (
									<Flex alignItems="center" justifyContent="center" gap={2.5} my={2.5}>
										<Text color={colours.blueGray[600]}>{t("modifyStudents.emptyState")}</Text>
									</Flex>
								) : (
									<>
										{sortedStudents.map((student) => (
											<Flex
												key={student.stableIdV1}
												alignItems="center"
												justifyContent="space-between"
												py={2}
												px={3}
												_hover={{ bgColor: "#F7F9FC" }}
												rounded={"md"}
												transition={"background-color 0.2s"}
											>
												<Text fontSize={"14px"} color={colours.text.body}>
													{getUserDisplayTextForSelect(student)}
												</Text>
												<Tooltip
													label={t("modifyStudents.removeFromClassTooltip", { name: userDisplayText(student) })}
													aria-label={t("modifyStudents.removeFromClassTooltip", { name: userDisplayText(student) })}
												>
													<IconButton
														data-testid={`remove-student-button-${student.username}`}
														onClick={() => {
															handleRemoveStudent(student);
														}}
														aria-label={t("modifyStudents.removeStudentAriaLabel")}
														icon={<ChakraFAIcon icon="fa-x" variant="solid" />}
														bgColor="transparent"
														color={colours.danger.icon}
														size="sm"
														variant="unstyled"
														isDisabled={isSaving}
													/>
												</Tooltip>
											</Flex>
										))}
									</>
								)}
							</Box>
						</>
					)}
				</Flex>
			</ModalBody>
		</ClasswizeModal>
	);
}
